import { LegalLayout, LegalSection } from "../component/legal/LegalLayout";
import { siteConfig } from "../config/site";

export function RiskManagementPolicyPage() {
  return (
    <LegalLayout
      eyebrow="Policies"
      title="Risk Management Policy"
      description={`A robust framework to identify, assess, and mitigate risks for sustainable growth at ${siteConfig.companyName}.`}
    >
      <LegalSection heading="1. Preamble">
        <p>
          The Board of Directors of {siteConfig.companyName} has adopted
          this policy to establish practices for identifying, assessing,
          monitoring, and mitigating risks.
        </p>
        <p>
          This Risk Management Policy aims to minimize adverse impacts on
          business objectives while enhancing stakeholder value and
          sustaining long-term competitive advantage.
        </p>
      </LegalSection>

      <LegalSection heading="2. Purpose">
        <p>
          Framed in line with applicable RBI Master Directions for
          Non-Banking Financial Companies, as amended from time to time,
          this policy safeguards human and financial assets without
          restricting mission-critical activities, supported by effective
          risk management systems.
        </p>
      </LegalSection>

      <LegalSection heading="3. Principles">
        <ul className="list-disc space-y-2 pl-5">
          <li>Risk management must create and protect value.</li>
          <li>It must be integrated into organizational processes.</li>
          <li>It supports informed decision-making.</li>
          <li>It focuses on uncertainties around achieving objectives.</li>
          <li>It must be tailored to the business context.</li>
          <li>It must be dynamic, iterative, and responsive to change.</li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. Definitions">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="font-semibold text-[#1c1917]">Board:</span>{" "}
            Board of Directors of the Company.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">Company:</span>{" "}
            {siteConfig.companyName}.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">Directors:</span>{" "}
            Members of the Board.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">Policy:</span>{" "}
            This Risk Management Policy.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">RBI:</span>{" "}
            Reserve Bank of India.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="5. Policy Objectives">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            Continuously identify organizational risks affecting business
            operations.
          </li>
          <li>
            Protect shareholder rights and values through an effective risk
            management framework.
          </li>
          <li>
            Enhance and maintain risk management tools to support strategic
            and operational decisions.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="6. Risk Identification, Measurement, and Assessment">
        <p>
          Management, under Board supervision, implements the Risk
          Management Program and establishes identification processes.
          Risks are evaluated using qualitative and quantitative methods
          based on likelihood and impact, and categorized as inherent or
          residual.
        </p>
        <p className="font-semibold text-[#1c1917]">Key Risk Categories:</p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <span className="font-semibold text-[#1c1917]">
              Credit Risk:
            </span>{" "}
            The risk of financial loss arising from a borrower's failure to
            meet repayment obligations, managed through credit appraisal,
            eligibility criteria, and ongoing portfolio monitoring.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Operational Risk:
            </span>{" "}
            The risk of loss resulting from inadequate or failed internal
            processes, people, systems, or external events, including
            technology and cybersecurity risk.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Liquidity Risk:
            </span>{" "}
            The risk of the Company being unable to meet its financial
            obligations as they fall due, managed through asset-liability
            matching and maintenance of adequate liquidity buffers.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Market Risk:
            </span>{" "}
            The risk of loss arising from adverse movements in interest
            rates or other market factors affecting the Company's funding
            costs.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Compliance & Regulatory Risk:
            </span>{" "}
            The risk of legal or regulatory sanctions, financial loss, or
            reputational damage arising from failure to comply with
            applicable laws, regulations, and RBI directions.
          </li>
          <li>
            <span className="font-semibold text-[#1c1917]">
              Reputational Risk:
            </span>{" "}
            The risk of loss arising from negative stakeholder perception
            of the Company's conduct, products, or practices.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="7. Risk Monitoring and Reporting">
        <p>
          Identified risks are monitored on an ongoing basis by Management
          and reported periodically to the Risk Management Committee and
          the Board, along with the status of mitigation actions and any
          emerging risks that require attention.
        </p>
      </LegalSection>

      <LegalSection heading="8. Review of the Policy">
        <p>
          This policy shall be reviewed by the Board periodically, or as
          and when required, to ensure it remains aligned with the
          Company's risk profile, business strategy, and applicable
          regulatory requirements.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
